import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { 
  Crown, 
  AlertTriangle, 
  ArrowRight, 
  Sparkles,
  Check,
  FileText,
  Link2,
  Users,
  Building2
} from "lucide-react";

// Plan details for upgrade prompts
const PLAN_UPGRADES = {
  free: {
    current: "Free / Starter",
    next: "growth",
    nextName: "Growth",
    price: "$149/mo",
    benefits: [
      "20 invoices (vs 10)",
      "20 payment links (vs 10)",
      "20 customers/vendors each",
      "Tax Hub access (10 docs)",
      "DocuHub access",
      "2% platform fee (vs 2.5%)"
    ]
  },
  growth: {
    current: "Growth",
    next: "scale",
    nextName: "Scale",
    price: "$349/mo",
    benefits: [
      "Unlimited invoices",
      "Unlimited payment links",
      "Unlimited customers/vendors",
      "Unlimited Tax Hub documents",
      "1% platform fee (vs 2%)",
      "Priority support"
    ]
  },
  scale: {
    current: "Scale",
    next: "enterprise",
    nextName: "Enterprise",
    price: "$999/mo",
    benefits: [
      "Everything in Scale",
      "S-Corp Election access",
      "Bill Pay access",
      "Custom Stripe integration",
      "Auto-payout enabled",
      "Dedicated team"
    ]
  }
};

// Feature labels
const FEATURE_LABELS = {
  invoices: { label: "Invoices", icon: FileText },
  payment_links: { label: "Payment Links", icon: Link2 },
  customers: { label: "Customers", icon: Users },
  vendors: { label: "Vendors", icon: Building2 },
  tax_hub_documents: { label: "Tax Hub Documents", icon: FileText }
};

export default function UpgradeLimitModal({ 
  open, 
  onClose, 
  feature, 
  currentPlan = "free",
  currentUsage = 0,
  limit = 0 
}) {
  const upgradeInfo = PLAN_UPGRADES[currentPlan] || PLAN_UPGRADES.free;
  const featureInfo = FEATURE_LABELS[feature] || { label: feature, icon: FileText };
  const FeatureIcon = featureInfo.icon;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md rounded-2xl border-2 border-[#BFFF00]/30" data-testid="upgrade-limit-modal">
        <DialogHeader>
          <div className="flex flex-col items-center text-center">
            {/* Warning Icon */}
            <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mb-4 border-2 border-red-200">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            
            <DialogTitle className="text-xl font-bold font-['Manrope']">
              {featureInfo.label} Limit Reached
            </DialogTitle>
            
            <DialogDescription className="text-[#121212]/60 mt-2">
              You've used all {limit} {featureInfo.label.toLowerCase()} on your{" "}
              <span className="font-semibold text-[#121212]">{upgradeInfo.current}</span> plan.
            </DialogDescription>
          </div>
        </DialogHeader>

        {/* Current Usage Display */}
        <div className="flex items-center justify-center gap-3 py-4">
          <div className="flex items-center gap-2 px-4 py-2 bg-red-50 rounded-full border border-red-200">
            <FeatureIcon className="w-4 h-4 text-red-500" />
            <span className="font-bold text-red-600">{currentUsage}</span>
            <span className="text-red-500">/</span>
            <span className="font-bold text-red-600">{limit}</span>
          </div>
        </div>

        {/* Upgrade Benefits */}
        <div className="p-4 bg-gradient-to-br from-[#F7FFE5] to-[#EDFFC2] rounded-2xl border-2 border-[#BFFF00]/30">
          <div className="flex items-center gap-2 mb-3">
            <Crown className="w-5 h-5 text-[#121212]" />
            <span className="font-bold text-[#121212]">
              Upgrade to {upgradeInfo.nextName}
            </span>
            <Badge className="bg-[#BFFF00] text-[#121212] ml-auto">
              {upgradeInfo.price}
            </Badge>
          </div>
          
          <ul className="space-y-2">
            {upgradeInfo.benefits.slice(0, 4).map((benefit, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm text-[#121212]/80">
                <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                {benefit}
              </li>
            ))}
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 mt-2">
          <Link to="/dashboard/plans" onClick={() => onClose(false)}>
            <Button className="w-full bg-[#121212] hover:bg-[#2a2a2a] rounded-full font-semibold h-12">
              <Sparkles className="w-4 h-4 mr-2" />
              Upgrade Now
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Button 
            variant="outline" 
            onClick={() => onClose(false)} 
            className="rounded-full"
          >
            Maybe Later
          </Button>
        </div>

        {/* Help Text */}
        <p className="text-xs text-center text-gray-500 mt-2">
          Need a custom plan?{" "}
          <a 
            href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ07qBLS9ridhleyYbHgoh8tK2GytA06zF8p0i5zF3IfiDRKXQGW17EZmfPsPcjIyZXiw-BjNNVw" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[#121212] font-medium hover:underline"
          >
            Talk to our team
          </a>
        </p>
      </DialogContent>
    </Dialog>
  );
}
